/**
 * JSON Schema 验证工具
 *
 * 提供配置块数据的统一验证接口
 */
import Ajv, { ErrorObject, ValidateFunction } from 'ajv'
import addFormats from 'ajv-formats'
import { getSchemaByType, ConfigType } from '@/schemas/config-types'

// 创建 Ajv 实例（全局单例）
const ajv = new Ajv({
  allErrors: true,        // 返回所有错误，不只是第一个
  verbose: true,          // 包含详细的错误信息
  $data: true,           // 支持 $data 引用
  removeAdditional: false, // 不自动删除额外属性
})

// 添加格式验证（如 uri, email 等）
addFormats(ajv)

// 缓存编译后的验证函数
const validateFnCache = new Map<string, ValidateFunction>()

/**
 * 验证结果
 */
export interface ValidationResult {
  valid: boolean
  errors?: ValidationError[]
}

/**
 * 验证错误（友好格式）
 */
export interface ValidationError {
  field: string         // 错误字段路径
  message: string       // 友好的错误信息
  value?: any          // 错误的值
  expectedType?: string // 期望的类型
}

/**
 * 批量验证结果
 */
export interface BatchValidationResult {
  valid: boolean
  totalCount: number
  validCount: number
  invalidCount: number
  results: Array<{
    index: number
    config_key?: string
    config_type?: string
    valid: boolean
    errors?: ValidationError[]
  }>
}

/**
 * 验证配置块数据
 *
 * @param configType - 配置类型（npc, quest, item 等）
 * @param data - 要验证的配置数据
 * @returns 验证结果
 *
 * @example
 * ```typescript
 * const result = validateConfigData('npc', {
 *   npc_id: 'npc_test',
 *   display_name: '测试 NPC',
 * })
 *
 * if (!result.valid) {
 *   console.error('验证失败:', result.errors)
 * }
 * ```
 */
export function validateConfigData(
  configType: ConfigType | string,
  data: any
): ValidationResult {
  // 获取对应的 Schema
  const schema = getSchemaByType(configType)

  if (!schema) {
    return {
      valid: false,
      errors: [{
        field: 'configType',
        message: `不支持的配置类型: ${configType}`,
      }],
    }
  }

  // 从缓存获取或编译验证函数
  let validate = validateFnCache.get(configType)
  if (!validate) {
    validate = ajv.compile(schema)
    validateFnCache.set(configType, validate)
  }

  // 执行验证
  const valid = validate(data)

  if (valid) {
    return { valid: true }
  }

  // 转换错误为友好格式
  const errors = formatValidationErrors(validate.errors || [])

  return {
    valid: false,
    errors,
  }
}

/**
 * 批量验证配置块
 *
 * @param items - 配置块数组
 * @returns 批量验证结果
 *
 * @example
 * ```typescript
 * const result = validateBatch([
 *   { config_type: 'npc', config_key: 'npc_001', config_data: {...} },
 *   { config_type: 'quest', config_key: 'quest_001', config_data: {...} },
 * ])
 *
 * console.log(`验证通过: ${result.validCount}/${result.totalCount}`)
 * ```
 */
export function validateBatch(
  items: Array<{
    config_type: string
    config_key?: string
    config_data: any
  }>
): BatchValidationResult {
  const results = items.map((item, index) => {
    const result = validateConfigData(item.config_type, item.config_data)

    return {
      index,
      config_key: item.config_key,
      config_type: item.config_type,
      valid: result.valid,
      errors: result.errors,
    }
  })

  const validCount = results.filter(r => r.valid).length
  const invalidCount = results.length - validCount

  return {
    valid: invalidCount === 0,
    totalCount: results.length,
    validCount,
    invalidCount,
    results,
  }
}

/**
 * 格式化验证错误为友好的错误信息
 *
 * @param errors - Ajv 原始错误对象
 * @returns 友好的错误信息数组
 */
function formatValidationErrors(errors: ErrorObject[]): ValidationError[] {
  return errors.map(err => {
    const field = err.instancePath.replace(/^\//, '').replace(/\//g, '.') || 'root'
    let message = ''
    let expectedType: string | undefined

    switch (err.keyword) {
      case 'required':
        message = `缺少必填字段: ${err.params.missingProperty}`
        break

      case 'type':
        expectedType = err.params.type
        message = `字段类型错误: 期望 ${expectedType}，实际 ${typeof err.data}`
        break

      case 'pattern':
        message = `格式错误: 不符合规则 ${err.params.pattern}`
        break

      case 'minLength':
        message = `长度不足: 最少需要 ${err.params.limit} 个字符`
        break

      case 'maxLength':
        message = `长度超限: 最多允许 ${err.params.limit} 个字符`
        break

      case 'minimum':
        message = `数值过小: 最小值为 ${err.params.limit}`
        break

      case 'maximum':
        message = `数值过大: 最大值为 ${err.params.limit}`
        break

      case 'enum':
        message = `值不在允许范围内: 允许的值为 ${err.params.allowedValues.join(', ')}`
        break

      case 'additionalProperties':
        message = `不允许的额外字段: ${err.params.additionalProperty}`
        break

      case 'minItems':
        message = `数组元素不足: 最少需要 ${err.params.limit} 个元素`
        break

      case 'maxItems':
        message = `数组元素过多: 最多允许 ${err.params.limit} 个元素`
        break

      case 'uniqueItems':
        message = `数组元素重复: 索引 ${err.params.i} 和 ${err.params.j} 的值相同`
        break

      default:
        message = err.message || '验证失败'
    }

    return {
      field,
      message,
      value: err.data,
      expectedType,
    }
  })
}

/**
 * 获取验证错误的摘要信息
 *
 * @param errors - 验证错误数组
 * @returns 错误摘要字符串
 *
 * @example
 * ```typescript
 * const summary = getValidationErrorSummary(result.errors)
 * // 输出: "发现 3 个错误: display_name (缺少必填字段), personality (格式错误), ..."
 * ```
 */
export function getValidationErrorSummary(errors: ValidationError[]): string {
  if (!errors || errors.length === 0) {
    return '无错误'
  }

  const errorCount = errors.length
  const errorList = errors
    .slice(0, 3)  // 只显示前 3 个错误
    .map(e => `${e.field} (${e.message})`)
    .join(', ')

  const suffix = errorCount > 3 ? `, ... 等 ${errorCount} 个错误` : ''

  return `发现 ${errorCount} 个错误: ${errorList}${suffix}`
}

/**
 * 清除验证函数缓存
 *
 * 当 Schema 定义更新时，应该调用此方法清除缓存
 */
export function clearValidationCache() {
  validateFnCache.clear()
}

/**
 * 获取配置类型的 Schema 示例数据
 *
 * @param configType - 配置类型
 * @returns Schema 示例数据（从 examples 字段提取）
 */
export function getSchemaExample(configType: ConfigType | string): any {
  const schema = getSchemaByType(configType)

  if (!schema || !schema.properties) {
    return null
  }

  const example: any = {}

  // 遍历 properties 提取示例值
  for (const [key, prop] of Object.entries(schema.properties)) {
    if (typeof prop === 'object' && prop !== null) {
      const p = prop as any

      // 优先使用 examples 字段
      if (p.examples && Array.isArray(p.examples) && p.examples.length > 0) {
        example[key] = p.examples[0]
      }
      // 其次使用 default 字段
      else if ('default' in p) {
        example[key] = p.default
      }
      // 最后根据类型生成默认值
      else if (schema.required && schema.required.includes(key)) {
        example[key] = getDefaultValueByType(p)
      }
    }
  }

  return example
}

/**
 * 根据类型生成默认值
 */
function getDefaultValueByType(prop: any): any {
  switch (prop.type) {
    case 'string':
      return prop.pattern ? '' : 'example'
    case 'number':
    case 'integer':
      return prop.minimum || 0
    case 'boolean':
      return false
    case 'array':
      return []
    case 'object':
      return {}
    default:
      return null
  }
}
